import type {
  CreateWorkspaceDetails,
  DocumentType,
  Label,
  MyWorkspaceMetadata,
} from "../../../state/workspaces/types";
import type { GlobalAccessType } from "../global.access";

export type WorkspaceModule = {
  name: string;
  configuration?: object;
};

export type WorkspacePageSize = {
  width: number;
  height: number;
};

export type CreateWorkspaceRequestMetadata = CreateWorkspaceDetails & {
  workspaceName: string;
  workspaceType: DocumentType;
  pageSize?: WorkspacePageSize;
  schemaVersion: number;
  labels?: Label[];
};

export type DuplicateWorkspaceRequestMetadata = {
  workspaceName: string;
  containerId?: string;
  globalAccess: GlobalAccessType;
  password: string | null;
};

export function isMyWorkspaceMetadata(
  obj: unknown,
): obj is MyWorkspaceMetadata {
  const ret =
    typeof obj === "object" &&
    obj !== null &&
    "containerId" in obj &&
    (obj.containerId === null || typeof obj.containerId === "string") &&
    "createTime" in obj &&
    (obj.createTime === null || typeof obj.createTime === "string") &&
    "hasPassword" in obj &&
    typeof obj.hasPassword === "boolean" &&
    "id" in obj &&
    typeof obj.id === "number" &&
    "isHidden" in obj &&
    (obj.isHidden === null || typeof obj.isHidden === "boolean") &&
    "labels" in obj &&
    (obj.labels === null || Array.isArray(obj.labels)) &&
    "lastAccess" in obj &&
    (obj.lastAccess === null || typeof obj.lastAccess === "string") &&
    "lastModified" in obj &&
    (obj.lastModified === null || typeof obj.lastModified === "string") &&
    "module" in obj &&
    obj.module !== null &&
    typeof obj.module === "object" &&
    "name" in obj.module &&
    (obj.module.name === null || typeof obj.module.name === "string") &&
    "myPermissions" in obj &&
    typeof obj.myPermissions === "object" &&
    obj.myPermissions !== null &&
    "canRead" in obj.myPermissions &&
    typeof obj.myPermissions.canRead === "boolean" &&
    "canWrite" in obj.myPermissions &&
    typeof obj.myPermissions.canWrite === "boolean" &&
    "canAdmin" in obj.myPermissions &&
    typeof obj.myPermissions.canAdmin === "boolean" &&
    "canExport" in obj.myPermissions &&
    typeof obj.myPermissions.canExport === "boolean" &&
    "canGrow" in obj.myPermissions &&
    typeof obj.myPermissions.canGrow === "boolean" &&
    "canLeave" in obj.myPermissions &&
    typeof obj.myPermissions.canLeave === "boolean" &&
    "orgId" in obj &&
    typeof obj.orgId === "string" &&
    "schemaVersion" in obj &&
    (obj.schemaVersion === null || typeof obj.schemaVersion === "number") &&
    "templateId" in obj &&
    (obj.templateId === null || typeof obj.templateId === "string") &&
    "weight" in obj &&
    (obj.weight === null || typeof obj.weight === "number") &&
    "workspaceId" in obj &&
    typeof obj.workspaceId === "string" &&
    "workspaceName" in obj &&
    (obj.workspaceName === null || typeof obj.workspaceName === "string") &&
    "workspaceType" in obj &&
    typeof obj.workspaceType === "string" &&
    (
      [
        "DELETED",
        "EXPERIMENTAL",
        "FLOW",
        "HOYLU",
        "PROCESS",
        "SKETCH",
      ] as DocumentType[]
    ).includes(obj.workspaceType as DocumentType);

  if (!ret) {
    console.warn("Invalid workspace object", obj);
  }
  return ret;
}

export interface MyWorkspacesResponse {
  workspaces: MyWorkspaceMetadata[] | null;
  offset: number | null;
}

export function isMyWorkspaceResponse(
  obj: unknown,
): obj is MyWorkspacesResponse {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "workspaces" in obj &&
    // NOTE: is not checking deeply for MyWorkspaceMetadata on purpose to allow those to be fixed up
    (obj.workspaces === null || Array.isArray(obj.workspaces)) &&
    "offset" in obj &&
    (obj.offset === null || typeof obj.offset === "number")
  );
}

export function assertMyWorkspaceResponse(
  obj: unknown,
): asserts obj is MyWorkspacesResponse {
  if (!isMyWorkspaceResponse(obj)) {
    throw new Error("Invalid response");
  }
}

export function stripInvalidWorkspaces(
  response: MyWorkspacesResponse,
): MyWorkspacesResponse {
  return {
    ...response,
    workspaces: response.workspaces?.filter(isMyWorkspaceMetadata) ?? null,
  };
}
